import { buildResourceUsageMatrixData } from "src/Components/Board/1/Resources/ResourceTimeline/useResourceUsageMatrix";

/**
 * Merge gantt (full board) with `data` (Kanban-scoped) so task dates exist even when
 * only one slice was loaded (aligned with WbsFlow.mergeTasksForWbsBoardView).
 */
export function mergeGanttAndDataTasks(gantt, data) {
  const ganttList = Array.isArray(gantt) ? gantt : [];
  const dataList = Array.isArray(data) ? data : [];
  const dataById = new Map(
    dataList.filter((t) => t?._id).map((t) => [String(t._id), t]),
  );
  const ganttIds = new Set(
    ganttList.filter((t) => t?._id).map((t) => String(t._id)),
  );

  const merged = [];

  for (const g of ganttList) {
    if (!g?._id) continue;
    const id = String(g._id);
    const d = dataById.get(id);
    const depG = Array.isArray(g.Dependency) ? g.Dependency : [];
    const depD = d && Array.isArray(d.Dependency) ? d.Dependency : [];
    const dependency = depD.length > 0 ? depD : depG;

    merged.push({
      ...g,
      ...(d || {}),
      _id: g._id,
      TaskName: d?.TaskName ?? g.TaskName,
      Complete: d?.Complete !== undefined ? d.Complete : g.Complete,
      Status: d?.Status ?? g.Status,
      TaskMap: Array.isArray(d?.TaskMap) ? d.TaskMap : g.TaskMap,
      Dependency: dependency,
    });
  }

  for (const row of dataList) {
    if (!row?._id) continue;
    const id = String(row._id);
    if (ganttIds.has(id)) continue;
    merged.push({
      ...row,
      Dependency: Array.isArray(row.Dependency) ? row.Dependency : [],
    });
  }

  return merged;
}

/** Same merge as ProjectResources BoardResourceHandler. */
export function mergeBoardResourcesWithAssignments(resources, tasksResources) {
  if (!Array.isArray(resources)) return [];
  const tr = Array.isArray(tasksResources) ? tasksResources : [];
  return resources.map((resource) => {
    const body = { ...resource, TasksResources: [] };
    const findTaskRes = tr.filter(
      (item) => String(item.Resource?.ID) === String(resource._id),
    );
    if (findTaskRes.length) body.TasksResources = findTaskRes;
    return body;
  });
}

function toLocalDayKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getTodayLocalDayKey() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return toLocalDayKey(d);
}

function dayKeyToLocalStartSec(dayKey) {
  const [y, m, d] = dayKey.split("-").map(Number);
  return Math.floor(new Date(y, m - 1, d, 0, 0, 0, 0).getTime() / 1000);
}

function dayKeyToLocalEndSecInclusive(dayKey) {
  const [y, m, d] = dayKey.split("-").map(Number);
  return Math.floor(new Date(y, m - 1, d, 23, 59, 59, 999).getTime() / 1000);
}

/**
 * Shortages on the resource timeline (local calendar days) from today onward only.
 * Shape matches API conflicts for ResourceAlertBanner / timeline row highlight.
 */
export function computeResourceConflictSummariesFromUsage(
  usageMap,
  detailMap,
  finalResources,
  todayKey,
) {
  const cutoff = todayKey ?? getTodayLocalDayKey();
  const conflicts = [];
  const resourcesById = new Map(
    finalResources.map((r) => [String(r._id ?? r.id ?? ""), r]),
  );

  for (const resourceId of Object.keys(usageMap)) {
    const resource = resourcesById.get(resourceId);
    if (!resource) continue;

    const capacity = Number(resource.Number ?? resource.capacity ?? 0);
    if (capacity <= 0) continue;

    const dayMap = usageMap[resourceId];
    const overloadDays = [];
    let peakShortage = 0;

    for (const dayKey of Object.keys(dayMap)) {
      if (dayKey < cutoff) continue;
      const count = dayMap[dayKey];
      if (count > capacity) {
        overloadDays.push(dayKey);
        peakShortage = Math.max(peakShortage, count - capacity);
      }
    }

    if (overloadDays.length === 0) continue;

    overloadDays.sort();
    const minDay = overloadDays[0];
    const maxDay = overloadDays[overloadDays.length - 1];

    const tasksInvolvedMap = new Map();
    for (const dk of overloadDays) {
      const details = detailMap[resourceId]?.[dk] ?? [];
      for (const row of details) {
        const tid = row.taskId != null ? String(row.taskId) : "";
        const tmid = row.taskMapId != null ? String(row.taskMapId) : "";
        const key = `${tid}-${tmid}`;
        if (tasksInvolvedMap.has(key)) continue;
        tasksInvolvedMap.set(key, {
          taskId: tid,
          taskName: row.name,
          taskMapId: tmid || null,
          demand: row.amount,
        });
      }
    }

    conflicts.push({
      resourceId,
      resourceName: resource.Name ?? resource.name,
      resourceType: resource.Type,
      shortageAmount: peakShortage,
      timeRange: {
        from: dayKeyToLocalStartSec(minDay),
        to: dayKeyToLocalEndSecInclusive(maxDay),
      },
      tasksInvolved: Array.from(tasksInvolvedMap.values()),
    });
  }

  return conflicts;
}

export function selectBoardResourceConflictAlerts(state) {
  const resources = state.ResourcesReducer?.Resources ?? [];
  const tasksResources = state.ResourcesReducer?.TasksResources ?? [];
  const gantt = state.TasksStateReducer?.gantt;
  const data = state.TasksStateReducer?.data;

  if (!Array.isArray(resources) || resources.length === 0) {
    return [];
  }

  const mergedResources = mergeBoardResourcesWithAssignments(
    resources,
    tasksResources,
  );
  const rawTasks = mergeGanttAndDataTasks(gantt, data);

  const { usageMap, detailMap } = buildResourceUsageMatrixData(
    mergedResources,
    rawTasks,
  );

  return computeResourceConflictSummariesFromUsage(
    usageMap,
    detailMap,
    mergedResources,
    getTodayLocalDayKey(),
  );
}
